import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  Grid,
  Chip,
  Divider,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { ordersApi } from '../services/api';
import AdminRefundPanel from './AdminRefundPanel';
import StoreOrderSections from './StoreOrderSections';

const STATUS_COLORS = {
  PAID: 'success', COMPLETED: 'success', CONFIRMED: 'success',
  CANCELLED_BY_USER: 'error', CANCELLED_BY_PROVIDER: 'error',
  ACTIVE: 'primary', DISCUSSION: 'warning', CREATED: 'default',
  FINISHED: 'secondary',
};

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const chatEndRef = useRef(null);

  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [error, setError] = useState('');

  // Hoisted out of the effect so the refund panel can re-read the order after refunding —
  // paymentStatus changes, and the panel's own visibility depends on it.
  const fetchOrder = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await ordersApi.getOrderById(id);
      setOrder(data);
    } catch (err) {
      setError('Failed to load order details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  useEffect(() => {
    if (!id) return;
    const fetchMessages = async () => {
      try {
        setMessagesLoading(true);
        const data = await ordersApi.getOrderMessages(id);
        setMessages(Array.isArray(data) ? data : []);
      } catch (err) {
        // messages may not exist yet — not a hard error
        setMessages([]);
      } finally {
        setMessagesLoading(false);
      }
    };
    fetchMessages();
  }, [id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container sx={{ mt: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Back</Button>
        <Alert severity="error">{error || 'Order not found'}</Alert>
      </Container>
    );
  }

  // senderId is stored as currentUser.id.toString() — compare with order.user.id
  const customerId = order.user?.id?.toString();
  const isCustomerMessage = (msg) => msg.senderId === customerId;

  const formatServiceType = (type) =>
    type ? type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) : '-';

  const formatDate = (val) => val ? new Date(val).toLocaleString() : '-';

  const provider = order.sentToProviders?.[0];
  const providerUserId = provider?.user?.id?.toString();

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Back</Button>
      </Stack>

      <Typography variant="h4" gutterBottom>Order #{order.id}</Typography>

      {/* Only renders for a CAPTURED booking — see AdminRefundPanel. */}
      <AdminRefundPanel order={order} onRefunded={fetchOrder} />

      {/* Order Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Summary</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={order.status}
                color={STATUS_COLORS[order.status] || 'default'}
                size="small"
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Order Type</Typography>
            <Typography variant="body2">{order.orderType}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Payment</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                label={order.paymentStatus === 'OFFLINE' ? 'Offline' : (order.paymentStatus || '—')}
                size="small"
              />
              {order.offlinePaidAt && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Marked paid {formatDate(order.offlinePaidAt)}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Service Type</Typography>
            <Typography variant="body2">{formatServiceType(order.dogServiceType)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Price</Typography>
            <Typography variant="body2">
              {order.price
                ? `${order.price} ${order.currencyCode || ''}`
                : order.calculatedTotal
                  ? `${order.calculatedTotal} ${order.currencyCode || ''}`
                  : '-'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Request Date</Typography>
            <Typography variant="body2">{formatDate(order.requestDate)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Duration</Typography>
            <Typography variant="body2">{order.numOfDays} day(s)</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Created</Typography>
            <Typography variant="body2">{formatDate(order.createdAt)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">Updated</Typography>
            <Typography variant="body2">{formatDate(order.updatedAt)}</Typography>
          </Grid>
          {order.moreInfo && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Notes</Typography>
              <Typography variant="body2">{order.moreInfo}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Customer & Provider */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Customer</Typography>
            {order.user ? (
              <Stack spacing={0.5}>
                <Typography variant="body1">
                  <Link to={`/users/${order.user.id}`} style={{ fontWeight: 600 }}>
                    {order.user.name}
                  </Link>
                </Typography>
                {order.user.email && <Typography variant="body2" color="text.secondary">{order.user.email}</Typography>}
                {order.user.phone && <Typography variant="body2" color="text.secondary">{order.user.phone}</Typography>}
              </Stack>
            ) : <Typography variant="body2" color="text.secondary">No customer info</Typography>}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Provider</Typography>
            {provider ? (
              <Stack spacing={0.5}>
                <Typography variant="body1">
                  <Link to={`/providers/${provider.id}`} style={{ fontWeight: 600 }}>
                    {provider.title || provider.user?.name || `Provider #${provider.id}`}
                  </Link>
                </Typography>
                {provider.user?.email && <Typography variant="body2" color="text.secondary">{provider.user.email}</Typography>}
                {provider.providerType && <Chip label={provider.providerType} size="small" sx={{ mt: 0.5, alignSelf: 'flex-start' }} />}
              </Stack>
            ) : <Typography variant="body2" color="text.secondary">No provider assigned</Typography>}
          </Paper>
        </Grid>
      </Grid>

      {/* Store Order Items */}
      {order.orderType === 'STORE' && order.orderItems && order.orderItems.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Order Items</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {order.orderItems.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>{item.storeProduct?.name || `Item #${idx + 1}`}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">{item.unitPrice} {order.currencyCode}</TableCell>
                  <TableCell align="right">{item.totalPrice} {order.currencyCode}</TableCell>
                </TableRow>
              ))}
              {order.calculatedTotal && (
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{order.calculatedTotal} {order.currencyCode}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* STORE only: delivery, totals + commission, fulfilment, return, payout (each only when present). */}
      <StoreOrderSections order={order} />

      {/* Chat Messages */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Messages {messages.length > 0 && `(${messages.length})`}
        </Typography>

        {messagesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No messages yet.</Typography>
        ) : (
          <Box
            sx={{
              maxHeight: 500,
              overflowY: 'auto',
              p: 1,
              bgcolor: 'grey.50',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {messages.map((msg) => {
              const fromCustomer = isCustomerMessage(msg);
              const senderName = fromCustomer
                ? (order.user?.name || 'Customer')
                : (provider?.title || provider?.user?.name || 'Provider');

              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    flexDirection: fromCustomer ? 'row-reverse' : 'row',
                    alignItems: 'flex-end',
                    gap: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: fromCustomer ? 'primary.main' : 'grey.500',
                      fontSize: 12,
                      flexShrink: 0,
                    }}
                  >
                    {senderName[0]?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ maxWidth: '70%' }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', textAlign: fromCustomer ? 'right' : 'left', mb: 0.25 }}
                    >
                      {senderName}
                    </Typography>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: fromCustomer ? 'primary.main' : 'background.paper',
                        color: fromCustomer ? 'primary.contrastText' : 'text.primary',
                        boxShadow: 1,
                        borderTopRightRadius: fromCustomer ? 0 : 2,
                        borderTopLeftRadius: fromCustomer ? 2 : 0,
                      }}
                    >
                      {msg.type && msg.type !== 'TEXT' && (
                        <Chip
                          label={msg.type}
                          size="small"
                          sx={{
                            mb: 0.5,
                            height: 16,
                            fontSize: '0.6rem',
                            bgcolor: fromCustomer ? 'rgba(255,255,255,0.25)' : 'grey.200',
                            color: fromCustomer ? 'inherit' : 'text.secondary',
                          }}
                        />
                      )}
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {msg.body}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textAlign: 'right',
                          mt: 0.25,
                          opacity: 0.7,
                          fontSize: '0.65rem',
                        }}
                      >
                        {msg.sentAt ? new Date(msg.sentAt).toLocaleString() : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <div ref={chatEndRef} />
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default OrderDetails;
